import type { SlateConfiguration } from './types';

export const filterCompleteSlates = (slates: SlateConfiguration[]): SlateConfiguration[] =>
  slates.filter((slate) => slate.slateId && slate.slateId !== '' && slate.percentage !== null);

export const getAvailableSlateOptions = (
  slateOptions: Array<{ label: string; value: string; startDate?: string }>,
  allSlates: SlateConfiguration[],
  currentIndex: number
) => {
  if (currentIndex === 0) return slateOptions;

  const previousSlates = allSlates.slice(0, currentIndex);
  const lastSelectedSlate = previousSlates[previousSlates.length - 1];

  if (!lastSelectedSlate?.slateId) return slateOptions;

  const lastSelectedSlateOption = slateOptions.find(
    (option) => option.value === lastSelectedSlate.slateId
  );
  const lastSelectedStartDate = lastSelectedSlateOption?.startDate;

  if (!lastSelectedStartDate) return slateOptions;

  return slateOptions.filter((option) => {
    if (!option.startDate) return true;
    return new Date(option.startDate) > new Date(lastSelectedStartDate);
  });
};

export const getAvailablePercentageOptions = (
  percentageOptions: Array<{ label: string; value: number }>,
  allSlates: SlateConfiguration[],
  currentIndex: number
) => {
  if (currentIndex === 0) return percentageOptions;

  const previousSlate = allSlates[currentIndex - 1];
  if (!previousSlate || previousSlate.percentage === null) return percentageOptions;

  return percentageOptions.filter((option) => option.value < previousSlate.percentage);
};

export const getNextSlateValue = (
  slates: SlateConfiguration[],
  slateOptions: Array<{ label: string; value: string; startDate?: string }>,
  defaultSlate?: string
): string => {
  let nextSlateValue = defaultSlate || '';

  if (slates.length > 0) {
    const lastSlate = slates[slates.length - 1];
    if (lastSlate.slateId) {
      const lastSelectedSlateOption = slateOptions.find(
        (option) => option.value === lastSlate.slateId
      );
      const lastSelectedStartDate = lastSelectedSlateOption?.startDate;

      if (lastSelectedStartDate) {
        const availableSlates = slateOptions.filter((option) => {
          if (!option.startDate) return true;
          return new Date(option.startDate) > new Date(lastSelectedStartDate);
        });

        const sortedAvailableSlates = availableSlates.sort((a, b) => {
          if (!a.startDate || !b.startDate) return 0;
          return new Date(a.startDate).getTime() - new Date(b.startDate).getTime();
        });

        if (sortedAvailableSlates.length > 0) {
          nextSlateValue = sortedAvailableSlates[0].value;
        }
      }
    }
  }

  return nextSlateValue;
};

export const checkCanAddSlate = (
  slates: SlateConfiguration[],
  slateOptions: Array<{ label: string; value: string; startDate?: string }>,
  percentageOptions: Array<{ label: string; value: number }>,
  maxSlates: number
): { canAdd: boolean; hasAvailableSlates: boolean; hasAvailablePercentages: boolean } => {
  if (slates.length >= maxSlates) {
    return { canAdd: false, hasAvailableSlates: false, hasAvailablePercentages: false };
  }

  const lastSlate = slates[slates.length - 1];
  let hasAvailableSlates = true;

  if (lastSlate?.slateId) {
    const lastSelectedSlateOption = slateOptions.find(
      (option) => option.value === lastSlate.slateId
    );
    const lastSelectedStartDate = lastSelectedSlateOption?.startDate;

    if (lastSelectedStartDate) {
      const availableSlates = slateOptions.filter((option) => {
        if (!option.startDate) return true;
        return new Date(option.startDate) > new Date(lastSelectedStartDate);
      });
      hasAvailableSlates = availableSlates.length > 0;
    }
  }

  let hasAvailablePercentages = true;
  if (lastSlate?.percentage !== null && lastSlate?.percentage !== undefined) {
    const availablePercentages = percentageOptions.filter(
      (option) => option.value < lastSlate.percentage
    );
    hasAvailablePercentages = availablePercentages.length > 0;
  }

  const canAdd = hasAvailableSlates && hasAvailablePercentages;

  return { canAdd, hasAvailableSlates, hasAvailablePercentages };
};

export const checkCanDeleteSlate = (
  slate: SlateConfiguration,
  allSlates: SlateConfiguration[]
): boolean => {
  if (allSlates.length <= 1) return false;

  const isThisSlateComplete = slate.slateId && slate.slateId !== '' && slate.percentage !== null;

  if (!isThisSlateComplete) {
    return true;
  }

  const otherCompleteSlates = allSlates.filter(
    (s) => s.id !== slate.id && s.slateId && s.slateId !== '' && s.percentage !== null
  );

  return otherCompleteSlates.length > 0;
};
